import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { UpdateGroupRoleSuccess } from '@libs/src/groups/groups-store/groups.actions';
import { GroupRole } from '@libs/src/models/group.model';
import { IAppState } from '@main-client/src/app/app.state';
import { HubConfigRolesDrawerData } from '@main-client/src/app/hub-configuration/hub-config-roles/hub-config-roles-drawer/hub-config-roles-drawer-data';

import isUndefined from 'lodash-es/isUndefined';

interface HubConfigRolesForm {
  expert_status: FormControl<string>;
  inviteable: FormControl<boolean>;
  name: FormControl<string>;
  opted_into_matchmaking: FormControl<boolean>;
  searchable: FormControl<boolean>;
}

@Component({
  selector: 'app-hub-config-roles-drawer',
  templateUrl: './hub-config-roles-drawer.component.html',
})
export class HubConfigRolesDrawerComponent implements OnInit {
  @Input() data: HubConfigRolesDrawerData;
  form: FormGroup<HubConfigRolesForm>;
  isLoading = false;
  roleNameLimit = 100;
  tenantState = this.store.select('tenant');

  constructor(
    private readonly drawerRef: NzDrawerRef,
    private readonly fb: FormBuilder,
    private readonly groupsBackend: GroupsBackend,
    private readonly store: Store<IAppState>,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit() {
    const role = this.data.role || {
      defaults: {
        expert_status: 'approved',
        inviteable: true,
        opted_into_matchmaking: true,
        searchable: true,
      },
      name: '',
    };
    this.form = this.fb.group<HubConfigRolesForm>({
      expert_status: new FormControl(role.defaults.expert_status),
      inviteable: new FormControl(role.defaults.inviteable),
      name: new FormControl(role.name, Validators.required),
      opted_into_matchmaking: new FormControl(
        isUndefined(role.defaults.opted_into_matchmaking)
          ? true
          : role.defaults.opted_into_matchmaking,
      ),
      searchable: new FormControl(role.defaults.searchable),
    });
  }

  submitForm() {
    if (!this.form.valid) {
      return;
    }
    const formValue = this.form.value;
    const roleBody = {
      defaults: {
        expert_status: formValue.expert_status,
        inviteable: formValue.inviteable,
        opted_into_matchmaking: formValue.opted_into_matchmaking,
        searchable: formValue.searchable,
      },
      name: formValue.name.trim(),
    };
    this.isLoading = true;
    if (this.data.role && this.data.role._id) {
      this.groupsBackend
        .putRoles(this.data.group, this.data.role, roleBody)
        .pipe(
          tap((response: GroupRole[]) => {
            this.store.dispatch(
              UpdateGroupRoleSuccess({
                group: this.data.group,
                roles: response,
              }),
            );
            this.closeDrawer();
            this.isLoading = false;
          }),
          catchError((response: HttpErrorResponse) => {
            this.toastService.showError(response.error.message);
            this.isLoading = false;
            return EMPTY;
          }),
        )
        .subscribe();
      return;
    }
    this.groupsBackend
      .postRoles(this.data.group, roleBody)
      .pipe(
        tap((response: GroupRole[]) => {
          this.store.dispatch(
            UpdateGroupRoleSuccess({
              group: this.data.group,
              roles: response,
            }),
          );
          this.closeDrawer(response);
          this.isLoading = false;
        }),
        catchError((response: HttpErrorResponse) => {
          this.toastService.showError(response.error.message);
          this.isLoading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  closeDrawer(response?: GroupRole[]): void {
    this.drawerRef.close(response);
  }
}
